import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal/Modal';
import {
  MessageOutlined,
  WhatsAppOutlined,
  RightOutlined,
} from '@ant-design/icons';

import { AuthActions, DeviceAction } from '@portal/actions';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedTabs from '@portal/components/AdvancedTabs/AdvancedTabs';
import SmsCode from '@portal/components/SmsCode/SmsCode';
import Spinner from '@portal/components/Spinner/Spinner';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { TokenMethod } from '@portal/enum/device';
import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';

import './TokenMethodSelectionModal.less';

interface TokenMethodSelectionModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onConfirmCode: (code: string, method: TokenMethod) => void;
  title?: string;
  description?: string;
}

const TokenMethodSelectionModal = ({
  visible,
  setVisible,
  onConfirmCode,
  title,
  description,
}: TokenMethodSelectionModalProps) => {
  const dispatch = useDispatch();
  const [loadingMethod, setLoadingMethod] = useState<TokenMethod | null>(null);
  const [activeTab, setActiveTab] = useState('method');
  const [selectedMethod, setSelectedMethod] = useState<TokenMethod | null>(
    null
  );
  const [code, setCode] = useState('');

  const {
    device: { lastNumbers, error: deviceError },
  } = useReduxState();

  const handleSelectChannel = async (method: TokenMethod) => {
    setLoadingMethod(method);
    try {
      await dispatch(AuthActions.userCredentialUpdateToken(method));
      setSelectedMethod(method);
      setActiveTab('validation');
    } catch (error) {
      setLoadingMethod(null);
      setVisible(false);
    } finally {
      setLoadingMethod(null);
    }
  };

  const handleResend = async (method?: TokenMethod) => {
    const methodToUse = method || selectedMethod || TokenMethod.SMS;
    if (methodToUse) {
      try {
        await dispatch(AuthActions.userCredentialUpdateToken(methodToUse));
      } catch (error) {
        setVisible(false);
      }
    }
  };

  const handleConfirm = () => {
    if (code.length === 6 && selectedMethod) {
      try {
        onConfirmCode(code, selectedMethod);
        setVisible(false);
      } catch (error) {
        setVisible(false);
      }
    }
  };

  useEffect(() => {
    if (visible) {
      const fetchRegisterInfo = async () => {
        try {
          await dispatch(DeviceAction.getRegisterInformation());
          setActiveTab('method');
          setSelectedMethod(null);
          setCode('');
        } catch (error) {
          setVisible(false);
        }
      };
      fetchRegisterInfo();
    }
  }, [dispatch, visible, setVisible]);

  useEffect(() => {
    if (!visible) {
      setActiveTab('method');
      setSelectedMethod(null);
      setCode('');
    }
  }, [visible]);

  useEffect(() => {
    if (deviceError) {
      setVisible(false);
    }
  }, [deviceError, setVisible]);

  const tabs = [
    {
      key: 'method',
      label:
        translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.TAB_METHOD') || 'Método',
      children: (
        <>
          <ThemedText
            variant="h2"
            className="token-method-selection-modal__title"
          >
            {title || translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.TITLE')}
          </ThemedText>
          <ThemedText
            variant="body"
            className="token-method-selection-modal__description"
          >
            {description ||
              translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.DESCRIPTION')}
          </ThemedText>

          <div className="token-method-selection-modal__options">
            <div
              className={`token-method-selection-modal__option-card ${
                loadingMethod === TokenMethod.SMS
                  ? 'token-method-selection-modal__option-card--loading'
                  : ''
              }`}
              onClick={() =>
                !loadingMethod && handleSelectChannel(TokenMethod.SMS)
              }
              onKeyPress={(e) => {
                if (!loadingMethod && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleSelectChannel(TokenMethod.SMS);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={translate(
                'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON'
              )}
              data-testid="token-method-selection_sms"
            >
              <div className="token-method-selection-modal__option-card__icon token-method-selection-modal__option-card__icon--sms">
                <MessageOutlined />
              </div>
              <div className="token-method-selection-modal__option-card__content">
                <div className="token-method-selection-modal__option-card__title">
                  {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SMS_BUTTON')}
                </div>
                <div className="token-method-selection-modal__option-card__description">
                  {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE', {
                    lastNumbers: lastNumbers || '',
                  })}
                </div>
              </div>
              {loadingMethod === TokenMethod.SMS ? (
                <Spinner size="small" />
              ) : (
                <RightOutlined className="token-method-selection-modal__option-card__arrow" />
              )}
            </div>
            <div
              className={`token-method-selection-modal__option-card ${
                loadingMethod === TokenMethod.WHATSAPP
                  ? 'token-method-selection-modal__option-card--loading'
                  : ''
              }`}
              onClick={() =>
                !loadingMethod && handleSelectChannel(TokenMethod.WHATSAPP)
              }
              onKeyPress={(e) => {
                if (!loadingMethod && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleSelectChannel(TokenMethod.WHATSAPP);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={translate(
                'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
              )}
              data-testid="token-method-selection_whatsapp"
            >
              <div className="token-method-selection-modal__option-card__icon token-method-selection-modal__option-card__icon--whatsapp">
                <WhatsAppOutlined />
              </div>
              <div className="token-method-selection-modal__option-card__content">
                <div className="token-method-selection-modal__option-card__title">
                  {translate(
                    'PAGES.AUTH.TOKEN_CHANNEL_SELECTION.WHATSAPP_BUTTON'
                  )}
                </div>
                <div className="token-method-selection-modal__option-card__description">
                  {translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.SUBTITLE', {
                    lastNumbers: lastNumbers || '',
                  })}
                </div>
              </div>
              {loadingMethod === TokenMethod.WHATSAPP ? (
                <Spinner size="small" />
              ) : (
                <RightOutlined className="token-method-selection-modal__option-card__arrow" />
              )}
            </div>
          </div>
        </>
      ),
    },
    {
      key: 'validation',
      label:
        translate('PAGES.AUTH.TOKEN_CHANNEL_SELECTION.TAB_VALIDATION') ||
        'Validação',
      disabled: !selectedMethod,
      children: (
        <>
          <ThemedText
            variant="h2"
            className="token-method-selection-modal__title"
          >
            {translate(
              `PAGES.MY_ACCOUNT.TABS.CREDENTIALS.VALIDATE_${
                selectedMethod === TokenMethod.WHATSAPP ? 'WHATSAPP' : 'SMS'
              }`
            )}
          </ThemedText>
          <ThemedText
            variant="body"
            color="gray"
            className="token-method-selection-modal__description"
          >
            {translate(
              `PAGES.MY_ACCOUNT.TABS.CREDENTIALS.${
                selectedMethod === TokenMethod.WHATSAPP ? 'WHATSAPP' : 'SMS'
              }`
            )}
          </ThemedText>

          <div className="token-method-selection-modal__validation">
            <div className="token-method-selection-modal__code-container">
              <SmsCode
                code={code}
                setCode={setCode}
                onClickResend={handleResend}
                method={selectedMethod || TokenMethod.SMS}
              />
            </div>

            <div className="token-method-selection-modal__button-container">
              <AdvancedButton
                fullWidth
                text={translate('COMPONENTS.ADVANCED_BUTTON.CONFIRM')}
                onClick={handleConfirm}
                disabled={code.length < 6}
              />
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      footer={null}
      title={null}
      closable
      onCancel={() => setVisible(false)}
      width={640}
      className="token-method-selection-modal"
    >
      <div className="token-method-selection-modal__content">
        <AdvancedTabs
          items={tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>
    </Modal>
  );
};

export default TokenMethodSelectionModal;
